import React from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Coffee, MapPin, Clock, Star, ArrowRight, ShoppingCart } from 'lucide-react';
import GoogleMap from '../components/GoogleMap';
import { useOpeningHours } from '../contexts/OpeningHoursContext';
import { formatOpeningHours } from '../utils/openingHours';
import { useMenu } from '../contexts/MenuContext';
import Cart from '../components/Cart';
import Logo from '../components/Logo';
import OpenStatusBadge from '../components/OpenStatusBadge';

const HomePage: React.FC = () => {
  const { openingHours, loading } = useOpeningHours();
  const { getCartItemCount } = useMenu();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Check if cart should be shown (from URL)
  const showCart = searchParams.get('cart') === 'true';
  
  // Debug: Check if image loads
  React.useEffect(() => {
    const img = new Image();
    img.onload = () => console.log('✅ Banner image loaded successfully');
    img.onerror = () => console.log('❌ Banner image failed to load');
    img.src = '/banner-background.jpg';
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative text-white py-24 overflow-hidden" style={{
        minHeight: '100vh',
        width: '100%'
      }}>
        {/* Background image using img element */}
        <img 
          src="/banner-background.jpg" 
          alt="Coffee shop background"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            filter: 'blur(1px)',
            zIndex: 1
          }}
        />
        
        {/* Dark overlay */}
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.75)',
            zIndex: 2
          }}
        ></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-5xl mx-auto"
          >
            <Link to="/menu" className="inline-block mb-1">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ 
                  scale: 1, 
                  opacity: 1,
                  y: [0, -15, 0],
                  rotate: [0, 5, -5, 0]
                }}
                transition={{ 
                  scale: { duration: 0.6, delay: 0.2 },
                  opacity: { duration: 0.6, delay: 0.2 },
                  y: { 
                    duration: 3, 
                    repeat: Infinity, 
                    ease: "easeInOut",
                    delay: 0.8
                  },
                  rotate: {
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 0.8
                  }
                }}
                whileHover={{ scale: 1.1 }}
                className="cursor-pointer"
              >
                <Logo 
                  size="xl" 
                  className="h-16 w-16 sm:h-20 sm:w-20 md:h-24 md:w-24 lg:h-28 lg:w-28 mx-auto drop-shadow-2xl" 
                />
              </motion.div>
            </Link>
            
            <h1 className="font-bold mb-3 sm:mb-4 leading-tight drop-shadow-2xl">
              <span className="text-gray-200 text-xl sm:text-2xl md:text-3xl lg:text-4xl" style={{ textShadow: '0 4px 6px rgba(0, 0, 0, 0.9), 0 2px 4px rgba(0, 0, 0, 0.9)' }}>Welcome to </span><span className="text-red-500 drop-shadow-2xl text-3xl sm:text-4xl md:text-5xl lg:text-6xl" style={{ textShadow: '0 4px 6px rgba(0, 0, 0, 0.9), 0 2px 4px rgba(0, 0, 0, 0.9)' }}>Kiss Coffee</span>
            </h1>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl mb-2 sm:mb-3 text-gray-300 font-light" style={{ textShadow: '0 2px 4px rgba(0, 0, 0, 0.8)' }}>
              Experience the finest coffee and delicious food
            </p>
            <p className="text-xs sm:text-sm md:text-base lg:text-lg mb-4 sm:mb-5 text-red-300" style={{ textShadow: '0 2px 4px rgba(0, 0, 0, 0.8)' }}>
              in the heart of Minehead
            </p>
            
            {/* Open/Closed Status Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="flex justify-center mb-6 sm:mb-8"
            >
              <OpenStatusBadge />
            </motion.div>
            
            <motion.div 
              className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <Link
                to="/menu"
                className="group bg-gradient-to-r from-red-700 to-red-800 hover:from-red-800 hover:to-red-900 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-full text-sm sm:text-base font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-2xl inline-flex items-center justify-center"
              >
                View Menu
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/order"
                className="group border-2 border-red-600 text-red-300 hover:bg-red-600 hover:text-white px-4 sm:px-6 py-2 sm:py-3 rounded-full text-sm sm:text-base font-semibold transition-all duration-300 transform hover:scale-105 inline-flex items-center justify-center"
              >
                Order Now
                <img src="/logo.png" alt="Logo" className="h-4 w-4 ml-2 group-hover:rotate-12 transition-transform object-contain" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r from-gray-900 to-red-700 bg-clip-text text-transparent">
              Why Choose Us?
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              We're passionate about delivering exceptional coffee and food experiences that bring people together
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="group text-center p-6 rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
            >
              <div className="bg-gradient-to-br from-red-600 to-red-700 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <Coffee className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Premium Coffee</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Carefully sourced beans roasted to perfection for the ultimate coffee experience that will awaken your senses
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="group text-center p-6 rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
            >
              <div className="bg-gradient-to-br from-red-600 to-red-700 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <Star className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Fresh Food</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Delicious meals made with fresh, locally sourced ingredients that celebrate the best of British cuisine
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="group text-center p-6 rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
            >
              <div className="bg-gradient-to-br from-red-600 to-red-700 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <MapPin className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Great Location</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Conveniently located in the heart of Minehead on Friday Street, perfect for locals and visitors alike
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Location & Hours Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl font-bold mb-6">Visit Us</h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <MapPin className="h-6 w-6 text-red-500 mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Address</h3>
                    <p className="text-gray-600">
                      16 Friday Street<br />
                      Minehead, TA24 5UA
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <Clock className="h-5 w-5 sm:h-6 sm:w-6 text-red-500 mt-1" />
                  <div>
                    <h3 className="text-lg sm:text-xl font-semibold mb-2">Opening Hours</h3>
                    <div className="text-gray-600 space-y-1 text-sm sm:text-base">
                      {loading ? (
                        <p>Loading hours...</p>
                      ) : (
                        formatOpeningHours(openingHours).map(({ day, hours }) => (
                          <p key={day}>{day}: {hours}</p>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="h-96"
            >
              <GoogleMap />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-red-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl font-bold mb-4">Ready to Order?</h2>
            <p className="text-xl mb-8">
              Browse our menu and place your order for dine-in or takeaway
            </p>
            <Link
              to="/order"
              className="btn btn-secondary btn-lg inline-flex items-center"
            >
              Start Ordering
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Cart Button - Only show when cart has items */}
      {getCartItemCount() > 0 && (
        <div className="fixed top-20 sm:top-20 right-4 z-40">
          <button
            onClick={() => {
              const newParams = new URLSearchParams(searchParams);
              newParams.set('cart', 'true');
              setSearchParams(newParams);
            }}
            className="group bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-full font-medium transition-all duration-300 transform hover:scale-105 hover:shadow-xl inline-flex items-center relative text-sm shadow-lg"
          >
            <ShoppingCart className="h-4 w-4 mr-2 group-hover:rotate-12 transition-transform" />
            <span className="hidden sm:inline">View Cart</span>
            <span className="sm:hidden">Cart</span>
            <span className="absolute -top-2 -right-2 bg-white text-red-600 text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold shadow-md border border-red-600">
              {getCartItemCount()}
            </span>
          </button>
        </div>
      )}

      {/* Cart Modal */}
      {showCart && (
        <Cart onClose={() => navigate(-1)} />
      )}
    </div>
  );
};

export default HomePage;
